import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { formatDateTime } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { queryClient } from "@impero-com/common/utils/query_client";
import { canGoBack } from "@impero-com/common/utils/url";
import {
    emitNotification,
    FormLabel,
    renderMarkdown,
    useInterval,
    useBelowBreakpoint,
    CollapseTransition,
    Divider,
    Section,
    SectionContent,
    CollapseContentSection,
    CollapseTextSection,
    Button,
} from "@impero-com/ui";
import { Popover, Typography } from "antd";
import { Control, FieldErrors, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import type {
    ControlActivityOverviewListDto,
    FieldGroupAndExtendedFieldsForTestingControlReviewerFieldKindAndTestingControlReviewerAnswerKind,
    SavedBlob,
    TestingActivityCurrentStep,
    TestingActivityDto,
    TestingActivityKind,
} from "api/generated/model";
import { RHFRadioButtonGroupInput } from "components/form/rhfFields/rhfRadioButtonGroupInput";
import { StickyFooter } from "components/stickyFooter/footer";
import { TagList } from "components/tag/tagList";
import { translateMonth } from "org/control/list/utils";
import { getPendingTestingActivitiesQueryKey } from "org/dashboardPendingActivities/queries/usePendingTestingActivitiesQuery";
import { dashboardPendingActivitiesUrl } from "org/dashboardPendingActivities/urlUtils";

import { SelectedSamplesTable } from "./components/SelectedSamplesTable/SelectedSamplesTable";
import { ReadOnlyTasks } from "./components/Tasks/ReadOnlyTasks";
import { Tasks } from "./components/Tasks/Tasks";
import {
    FirstReviewerOptions,
    getFirstReviewerSubmissionOptions,
    getSecondReviewerSubmissionOptions,
    SecondReviewerOptions,
    TESTING_ACTIVITY_DESCRIPTION_ANCHOR,
    TESTING_ACTIVITY_RESPONSIBLE_ANCHOR,
    TESTING_ACTIVITY_REVIEWER_ANCHOR,
    TESTING_ACTIVITY_SECOND_REVIEWER_ANCHOR,
    useControlTestingCollectAnchorLabels,
} from "./consts";
import { PopoverContent } from "./PopoverContent/PopoverContent";
import { usePatchTestingActivityMutation } from "./queries/usePatchTestingActivityMutation";
import { useSubmitTestingActivityMutation } from "./queries/useSubmitTestingActivityMutation";
import {
    ActivitySample,
    SAMPLES_KEY,
    TestingActivityForm,
    useTestingActivitySchema,
} from "./schema";
import {
    toControlSamples,
    toActivitySampleValues,
    toStepFieldValues,
    formStateToUpdatableTestingActivity,
} from "./utils";
import * as theme from "./view.css";

const { Text } = Typography;

const UPDATE_DELAY = 1000;

interface TestingActivityViewProps {
    testingActivity: TestingActivityDto;
    controlActivitiesList: ControlActivityOverviewListDto;
    activityId: string;
}
export function TestingActivityView({
    testingActivity,
    controlActivitiesList,
    activityId,
}: TestingActivityViewProps) {
    const {
        content: { resource, actions },
        linkedResources: { tagCategories, blobs, tags },
    } = testingActivity;
    const {
        description,
        title,
        currentStep,
        samplingCriteria: { samplingTags, coveredPeriod },
        controlActivitiesSamples,
        activityKind, // should not destructure - needs to be narrowed
        controlConclusionFieldGroup,
        sampleConclusionFieldGroup: { fields: sampleConclusionFields },
        archived,
    } = resource;
    const {
        fields: controlConclusionFields,
        answers: controlConclusionAnswers,
    } = controlConclusionFieldGroup;

    const schema = useTestingActivitySchema(resource);

    const { control, handleSubmit, trigger, formState, getValues, reset } =
        useForm<TestingActivityForm>({
            resolver: zodResolver(schema),
            defaultValues: {
                fields: toStepFieldValues(
                    activityKind,
                    currentStep,
                    controlConclusionFieldGroup
                ),
                samples: toActivitySampleValues(
                    controlActivitiesSamples,
                    sampleConclusionFields
                ),
                submission: null,
            },
            resetOptions: {
                keepErrors: true,
            },
            mode: "onBlur",
        });

    const { mutateAsync: patchMutate, submittedAt: patchedAt } =
        usePatchTestingActivityMutation();

    const readOnly = actions.edit !== true || archived;
    const readOnlyOrNotResponsible = readOnly || currentStep !== "responsible";

    const samplingTagsOverview = samplingTags.map(
        (tagId) => tags.find((tag) => tag.id === tagId)!
    );
    const coveredPeriodLabel = `${translateMonth(coveredPeriod.start.month)} ${
        coveredPeriod.start.year
    } - ${translateMonth(coveredPeriod.end.month)} ${coveredPeriod.end.year}`;
    const { t } = useTranslation();
    const sideSection = (
        <div>
            <Section styleVariant="primary">
                <SectionContent>
                    <div className={theme.sideSectionContent}>
                        <div>
                            <FormLabel>
                                {t("ControlTestingActivityView_Period")}
                            </FormLabel>
                            <Text>{coveredPeriodLabel}</Text>
                        </div>

                        <div>
                            <FormLabel>
                                {t("ControlTestingActivityView_EntityToTest")}
                            </FormLabel>
                            <TagList
                                mode="LIST"
                                tags={samplingTagsOverview.filter(
                                    (tag) => tag.active
                                )}
                                tagCategories={tagCategories}
                            />
                        </div>
                        <CollapseTransition collapsed={patchedAt === 0}>
                            <Text type="secondary">
                                {t("ControlTestingActivityView_PatchedAt", {
                                    time: formatDateTime(new Date(patchedAt)),
                                })}
                            </Text>
                        </CollapseTransition>
                    </div>
                </SectionContent>
            </Section>
        </div>
    );

    const handlePatchSubmit = async () => {
        const data = formStateToUpdatableTestingActivity(
            getValues,
            formState,
            resource
        );
        if (!data) {
            return;
        }
        try {
            await patchMutate({ data, testingActivityId: activityId });
            reset(getValues(), {
                keepErrors: true,
                keepDirty: false,
            });
        } catch {
            reset();
        }
    };
    useInterval(() => {
        handlePatchSubmit();
    }, UPDATE_DELAY);

    const { mutateAsync: submitMutate, status: submitStatus } =
        useSubmitTestingActivityMutation();
    const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);

    const handleSubmitClick = handleSubmit(
        () => {
            setConfirmSubmitOpen(true);
            // While the submit confirmation is open, form is assumed to be in valid state
        },
        (error) => {
            console.error(error, getValues());
            emitNotification(
                "error",
                t("ControlTestingActivityView_SubmitValidationError")
            );
        }
    );
    const navigate = useNavigate();
    const handleSubmitConfirm = async () => {
        const submissionKind =
            currentStep === "responsible"
                ? "responsible"
                : getValues().submission;
        if (!submissionKind) {
            throw Error("Expected submissionKind to be set!");
        }
        try {
            await submitMutate({
                submissionKind,
                id: activityId,
                testingProgramId: resource.testingProgramId,
            });
            await queryClient.invalidateQueries({
                queryKey: getPendingTestingActivitiesQueryKey(),
            });
            if (canGoBack(history)) {
                navigate(-1);
            } else {
                navigate(dashboardPendingActivitiesUrl);
            }
        } catch {
            setConfirmSubmitOpen(false);
        }
    };
    const handlePopupVisibleChange = (visible: boolean) => {
        // Ignore true - controlled
        // Handle false - on click outside popover
        if (visible === false) {
            setConfirmSubmitOpen(false);
        }
    };

    const showCancelButton = canGoBack(history);
    const anchorLabels = useControlTestingCollectAnchorLabels(currentStep);
    const belowXl = useBelowBreakpoint("xl");

    return (
        <form className={theme.root}>
            <div className={theme.view}>
                <div className={theme.primaryContent}>
                    <CollapseTextSection
                        content={renderMarkdown(description)}
                        titleLabel={title}
                        level={4}
                        noContentLabel={t(
                            "ControlTestingActivityView_NoDescription"
                        )}
                        collapseLabel={t("ToggleExpand_Collapse")}
                        expandLabel={t("ToggleExpand_Expand")}
                        id={TESTING_ACTIVITY_DESCRIPTION_ANCHOR}
                    />

                    {belowXl && sideSection}

                    <CollapseContentSection
                        titleLabel={
                            anchorLabels[TESTING_ACTIVITY_RESPONSIBLE_ANCHOR]
                        }
                        level={4}
                        styleVariant="primary"
                        backgroundVariant={
                            readOnlyOrNotResponsible ? "readOnly" : "active"
                        }
                        id={TESTING_ACTIVITY_RESPONSIBLE_ANCHOR}
                    >
                        <SelectedSamplesTable
                            control={control}
                            sampleConclusionFields={sampleConclusionFields}
                            controlSamples={toControlSamples(
                                controlActivitiesList,
                                controlActivitiesSamples
                            )}
                            trigger={trigger}
                            errors={
                                formState.errors?.[SAMPLES_KEY] as FieldErrors<
                                    ActivitySample[]
                                >
                            }
                            testingActivityId={activityId}
                            readOnly={readOnlyOrNotResponsible}
                            savedBlobs={blobs}
                            controlActivitiesSamples={controlActivitiesSamples}
                            requestedRandomSampleCount={
                                testingActivity.content.resource
                                    .samplingCriteria.randomSampleCount
                            }
                        />
                        <Divider />
                        {!readOnlyOrNotResponsible ? (
                            <Tasks
                                fields={controlConclusionFields}
                                control={control}
                                name={"fields"}
                                savedBlobs={blobs}
                            />
                        ) : (
                            <ReadOnlyTasks
                                fields={controlConclusionFields}
                                answers={controlConclusionAnswers}
                                savedBlobs={blobs}
                            />
                        )}
                    </CollapseContentSection>

                    <ReviewerSections
                        blobs={blobs}
                        control={control}
                        currentStep={currentStep}
                        activityKind={activityKind}
                        readOnly={readOnly}
                    />
                </div>
                {!belowXl && sideSection}
            </div>
            {currentStep !== "completed" && !archived && (
                <StickyFooter>
                    <Popover
                        open={confirmSubmitOpen}
                        onOpenChange={handlePopupVisibleChange}
                        getPopupContainer={(element) =>
                            element.parentElement ?? document.body
                        }
                        trigger="click"
                        content={
                            <PopoverContent
                                onRemove={() => void handleSubmitConfirm()}
                                onCancel={() => setConfirmSubmitOpen(false)}
                                warnLabel={t(
                                    currentStep === "responsible"
                                        ? "ControlTestingActivityView_ResponsibleSubmitWarn"
                                        : "ControlTestingActivityView_ReviewerSubmitWarn"
                                )}
                            />
                        }
                    >
                        <Button
                            size="large"
                            variant="primary"
                            onClick={() => void handleSubmitClick()}
                            disabled={readOnly}
                            loading={submitStatus === "pending"}
                        >
                            {t("ControlTestingActivityView_SubmitButton")}
                        </Button>
                    </Popover>
                    {showCancelButton && (
                        <Button
                            size="large"
                            variant="default"
                            onClick={() => navigate(-1)}
                        >
                            {t("ControlTestingActivityView_CancelButton")}
                        </Button>
                    )}
                </StickyFooter>
            )}
        </form>
    );
}

interface ReviewerSectionsProps {
    activityKind: TestingActivityKind;
    currentStep: TestingActivityCurrentStep;
    blobs: SavedBlob[];
    control: Control<TestingActivityForm>;
    readOnly: boolean;
}
function ReviewerSections({
    activityKind,
    currentStep,
    blobs,
    control,
    readOnly,
}: ReviewerSectionsProps) {
    const anchorLabels = useControlTestingCollectAnchorLabels(currentStep);
    const { t } = useTranslation();
    const firstReviewerSubmitOptions = getFirstReviewerSubmissionOptions(
        activityKind,
        t
    );
    const secondReviewerSubmitOptions = getSecondReviewerSubmissionOptions(t);

    switch (activityKind.kind) {
        case "ResponsibleOnly":
            return null;
        case "WithFirstReviewer":
            switch (currentStep) {
                case "firstReviewer":
                    return (
                        <ReviewerSection
                            readOnly={readOnly}
                            blobs={blobs}
                            control={control}
                            fieldGroup={activityKind.firstReviewerFieldGroup}
                            titleLabel={
                                anchorLabels[TESTING_ACTIVITY_REVIEWER_ANCHOR]
                            }
                            id={TESTING_ACTIVITY_REVIEWER_ANCHOR}
                            submitOptions={firstReviewerSubmitOptions}
                        />
                    );

                case "responsible":
                case "completed":
                    return (
                        <ReviewerSection
                            readOnly={true}
                            blobs={blobs}
                            control={control}
                            fieldGroup={activityKind.firstReviewerFieldGroup}
                            titleLabel={
                                anchorLabels[TESTING_ACTIVITY_REVIEWER_ANCHOR]
                            }
                            id={TESTING_ACTIVITY_REVIEWER_ANCHOR}
                            submitOptions={firstReviewerSubmitOptions}
                        />
                    );
                case "secondReviewer":
                    throw new Error("Invalid state");
                default:
                    return assertNever(currentStep);
            }
        case "WithSecondReviewer":
            switch (currentStep) {
                case "firstReviewer":
                    return (
                        <>
                            <ReviewerSection
                                readOnly={readOnly}
                                blobs={blobs}
                                control={control}
                                fieldGroup={
                                    activityKind.firstReviewerFieldGroup
                                }
                                titleLabel={
                                    anchorLabels[
                                        TESTING_ACTIVITY_REVIEWER_ANCHOR
                                    ]
                                }
                                id={TESTING_ACTIVITY_REVIEWER_ANCHOR}
                                submitOptions={firstReviewerSubmitOptions}
                            />
                            <ReviewerSection
                                readOnly={true}
                                blobs={blobs}
                                control={control}
                                fieldGroup={
                                    activityKind.secondReviewerFieldGroup
                                }
                                titleLabel={
                                    anchorLabels[
                                        TESTING_ACTIVITY_SECOND_REVIEWER_ANCHOR
                                    ]
                                }
                                id={TESTING_ACTIVITY_SECOND_REVIEWER_ANCHOR}
                                submitOptions={secondReviewerSubmitOptions}
                            />
                        </>
                    );
                case "secondReviewer":
                    return (
                        <>
                            <ReviewerSection
                                readOnly={true}
                                blobs={blobs}
                                control={control}
                                fieldGroup={
                                    activityKind.firstReviewerFieldGroup
                                }
                                titleLabel={
                                    anchorLabels[
                                        TESTING_ACTIVITY_REVIEWER_ANCHOR
                                    ]
                                }
                                id={TESTING_ACTIVITY_REVIEWER_ANCHOR}
                                submitOptions={firstReviewerSubmitOptions}
                            />
                            <ReviewerSection
                                readOnly={readOnly}
                                blobs={blobs}
                                control={control}
                                fieldGroup={
                                    activityKind.secondReviewerFieldGroup
                                }
                                titleLabel={
                                    anchorLabels[
                                        TESTING_ACTIVITY_SECOND_REVIEWER_ANCHOR
                                    ]
                                }
                                id={TESTING_ACTIVITY_SECOND_REVIEWER_ANCHOR}
                                submitOptions={secondReviewerSubmitOptions}
                            />
                        </>
                    );
                case "responsible":
                case "completed":
                    return (
                        <>
                            <ReviewerSection
                                readOnly={true}
                                blobs={blobs}
                                control={control}
                                fieldGroup={
                                    activityKind.firstReviewerFieldGroup
                                }
                                titleLabel={
                                    anchorLabels[
                                        TESTING_ACTIVITY_REVIEWER_ANCHOR
                                    ]
                                }
                                id={TESTING_ACTIVITY_REVIEWER_ANCHOR}
                                submitOptions={firstReviewerSubmitOptions}
                            />
                            <ReviewerSection
                                readOnly={true}
                                blobs={blobs}
                                control={control}
                                fieldGroup={
                                    activityKind.secondReviewerFieldGroup
                                }
                                titleLabel={
                                    anchorLabels[
                                        TESTING_ACTIVITY_SECOND_REVIEWER_ANCHOR
                                    ]
                                }
                                id={TESTING_ACTIVITY_SECOND_REVIEWER_ANCHOR}
                                submitOptions={secondReviewerSubmitOptions}
                            />
                        </>
                    );
                default:
                    return assertNever(currentStep);
            }
        default:
            return assertNever(activityKind);
    }
}

interface ReviewerSectionProps {
    titleLabel: string;
    id: string;
    fieldGroup: FieldGroupAndExtendedFieldsForTestingControlReviewerFieldKindAndTestingControlReviewerAnswerKind;
    control: Control<TestingActivityForm>;
    blobs: SavedBlob[];
    readOnly: boolean;
    submitOptions: FirstReviewerOptions[] | SecondReviewerOptions[];
}
function ReviewerSection({
    id,
    titleLabel,
    fieldGroup,
    control,
    blobs,
    readOnly,
    submitOptions,
}: ReviewerSectionProps) {
    const { t } = useTranslation();
    const hasFields = fieldGroup.fields.length > 0;

    const tasks = readOnly ? (
        <ReadOnlyTasks
            answers={fieldGroup.answers}
            fields={fieldGroup.fields}
            savedBlobs={blobs}
        />
    ) : (
        <Tasks
            fields={fieldGroup.fields}
            control={control}
            name={"fields"}
            savedBlobs={blobs}
        />
    );

    return (
        <CollapseContentSection
            titleLabel={titleLabel}
            level={4}
            styleVariant="primary"
            backgroundVariant={readOnly ? "readOnly" : "active"}
            initialCollapsed={readOnly}
            id={id}
        >
            <>
                {hasFields ? (
                    tasks
                ) : (
                    <Text type="secondary">
                        {t("ControlTestingActivityView_NoTasks")}
                    </Text>
                )}
                {!readOnly && (
                    <>
                        <Divider />
                        <RHFRadioButtonGroupInput
                            label={t(
                                "ControlTestingActivityView_SubmissionLabel"
                            )}
                            control={control}
                            name="submission"
                            options={submitOptions}
                        />
                    </>
                )}
            </>
        </CollapseContentSection>
    );
}
